import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { LogIn, LogOut, Clock } from 'lucide-react';

function fmt(iso) { if (!iso) return '—'; return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
function durationHrs(a, b) {
  if (!a || !b) return '—';
  const ms = new Date(b) - new Date(a);
  const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}

export default function Attendance() {
  const { user } = useAuth();
  const canViewAll = ['admin', 'hr'].includes(user?.role);
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadMine = async () => {
    const [t, h] = await Promise.all([api.get('/attendance/today'), api.get('/attendance/me?limit=30')]);
    setToday(t.data); setHistory(h.data);
  };
  const loadAll = async () => {
    if (!canViewAll) return;
    const { data } = await api.get('/attendance/all');
    setAll(data);
  };
  useEffect(() => { loadMine(); loadAll(); }, []);

  const doCheckin = async () => {
    setBusy(true);
    try { await api.post('/attendance/checkin', {}); toast.success('Checked in'); loadMine(); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doCheckout = async () => {
    setBusy(true);
    try { await api.post('/attendance/checkout', {}); toast.success('Checked out — have a great day!'); loadMine(); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Time tracking</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Attendance</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between" data-testid="attendance-actions-card">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Today</div>
          <div className="font-heading text-2xl font-bold text-slate-900 mt-1">
            {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
          </div>
          <div className="flex gap-6 mt-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Check-in</div>
              <div className="font-semibold text-slate-900 mt-0.5" data-testid="today-checkin-time">{fmt(today?.check_in)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Check-out</div>
              <div className="font-semibold text-slate-900 mt-0.5" data-testid="today-checkout-time">{fmt(today?.check_out)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Duration</div>
              <div className="font-semibold text-slate-900 mt-0.5">{durationHrs(today?.check_in, today?.check_out)}</div>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={doCheckin} disabled={busy || !!today?.check_in} data-testid="checkin-button"
                  className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-12">
            <LogIn className="w-4 h-4 mr-2" strokeWidth={1.8} /> Check in
          </Button>
          <Button onClick={doCheckout} disabled={busy || !today?.check_in || !!today?.check_out} data-testid="checkout-button"
                  variant="outline" className="rounded-full px-6 h-12">
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.8} /> Check out
          </Button>
        </div>
      </div>

      <Tabs defaultValue="mine">
        <TabsList className="rounded-full bg-slate-100 p-1 h-auto">
          <TabsTrigger value="mine" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-my-attendance">
            My history
          </TabsTrigger>
          {canViewAll && (
            <TabsTrigger value="all" className="rounded-full px-5 py-2 data-[state=active]:bg-[#3D4A8E] data-[state=active]:text-white" data-testid="tab-all-attendance">
              Today's team
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="mine" className="mt-5">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="my-attendance-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="text-left p-4">Date</th><th className="text-left p-4">Check-in</th><th className="text-left p-4">Check-out</th><th className="text-left p-4">Duration</th></tr>
              </thead>
              <tbody>
                {history.map(r => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-4 font-medium text-slate-900">{r.date}</td>
                    <td className="p-4 text-slate-600">{fmt(r.check_in)}</td>
                    <td className="p-4 text-slate-600">{fmt(r.check_out)}</td>
                    <td className="p-4 text-slate-600">{durationHrs(r.check_in, r.check_out)}</td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr><td colSpan={4} className="p-8 text-center text-slate-400 flex items-center justify-center gap-2"><Clock className="w-4 h-4" /> No records yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {canViewAll && (
          <TabsContent value="all" className="mt-5">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="all-attendance-list">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left p-4">Employee</th><th className="text-left p-4">Department</th><th className="text-left p-4">Check-in</th><th className="text-left p-4">Check-out</th></tr>
                </thead>
                <tbody>
                  {all.map(r => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="p-4">
                        <div className="font-medium text-slate-900">{r.name}</div>
                        <div className="text-xs text-slate-500">{r.email}</div>
                      </td>
                      <td className="p-4 text-slate-600">{r.department || '—'}</td>
                      <td className="p-4 text-slate-600">{fmt(r.check_in)}</td>
                      <td className="p-4 text-slate-600">{fmt(r.check_out)}</td>
                    </tr>
                  ))}
                  {all.length === 0 && (
                    <tr><td colSpan={4} className="p-8 text-center text-slate-400">No check-ins yet today.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
